import { api } from '../axios';

import {
  EvaluationResponse,
  EvaluationWhere,
  Evaluation,
  EvaluationTree,
  SubjectsResponse,
  Messages,
  BestQuestionResponse,
  BestQuestionParans,
  ContestSubject,
  MongoAttributes,
} from '../../types';

import Mongo from './Mongo';
import Authentication from './Authentication';
import Cycle from './Cycle';
import StudyControl from './StudyControl';

import { AxiosResponse } from 'axios';
import NotifyHelper from '../NotifyHelper';
import Journey from './Journey';

const HISTORY_EVALUATION_ENDPOINT = '/history/evaluation';
const BEST_QUESTION_ENDPOINT = 'questions/best';

export interface FetchEvaluationParams {
  headers?: Record<string, string>;
  where?: EvaluationWhere;
  contestSubjects?: boolean;
  suggested?: boolean;
  page?: number;
  max?: number;
}

class Evaluations extends Mongo {
  public fetchSubjects(subjectsQuery?: string[]): SubjectsResponse | undefined {
    try {
      const loginData = Authentication.loggedUser();
      if (!loginData) return;
      const response = {
        data: loginData.contest.subjects
          .filter((s) => s.evaluation !== false)
          .map((subject: ContestSubject) => {
            return {
              title: subject.subject,
              topics: subject.topics.map((topic) => {
                return topic.name;
              }),
              number_of_questions: subject.number_of_questions,
            };
          })
          .sort((a, b) => b.number_of_questions - a.number_of_questions),
      };
      if (subjectsQuery?.length) {
        response.data = response.data.filter((subject: { title: unknown }) =>
          subjectsQuery.includes(subject.title as string)
        );
      }
      return response;
    } catch (error) {
      NotifyHelper.notifyError(Messages.FETCH_LOCAL_CONTEST_SUBJECTS_ERROR);
      console.error(error);
    }
  }
  public async fetchSuggestedSubjects(): Promise<string[] | undefined> {
    try {
      const schedules = await StudyControl.fetchCurrentStudy();
      let suggestedSubjects: string[] = [];
      if (schedules?.data._items.length) {
        const {
          data: { _items },
        } = schedules;
        const study = _items[0];
        if (!study._id) return;
        const suggested = await Cycle.fetchCycles(study._id);
        if (suggested?.data._items.length) {
          suggestedSubjects = suggested?.data._items[0].subjects.map(
            (subject: { subject: string }) => {
              return subject.subject;
            }
          );
        }
        return suggestedSubjects;
      }
    } catch (error) {
      console.error(error);
    }
  }
  public async fetchEvaluations({
    headers,
    where,
    page,
    max,
    sortBy = [['_created', 'desc']],
  }: {
    headers?: { [key: string]: string };
    where: EvaluationWhere;
    page?: number;
    max?: number;
    sortBy?: [string, string][];
  }): Promise<EvaluationResponse | undefined> {
    try {
      const loginData = Authentication.loggedUser();
      if (!loginData) return;
      const flattenQuery = this.flattenMongoQuery({
        query: {
          ...where,
          user_email: loginData.user.email,
        },
      }) as unknown;
      const response: EvaluationResponse = await api.get(
        HISTORY_EVALUATION_ENDPOINT,
        {
          params: {
            page,
            max_results: max,
            where: flattenQuery,
            sort: this.parseSortArr(sortBy),
          },
          headers,
        }
      );
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  public async fetchEvaluationById(eid: string) {
    try {
      const user = Authentication.loggedUser();
      if (!user) return;
      const response: AxiosResponse<Evaluation> = await api.get(
        `${HISTORY_EVALUATION_ENDPOINT}/${eid}`
      );
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  public async fetchEvaluationHistoryTree(): Promise<
    EvaluationTree[] | undefined
  > {
    try {
      let subjectList: string[] = [];
      const subjects = this.fetchSubjects();
      subjectList = subjects?.data.map((subject) => subject.title) || [];

      const response = await this.fetchEvaluations({
        where: {
          subject: {
            $in: subjectList,
          },
        },
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response) return;
      const evaluationTree = [] as EvaluationTree[];

      subjectList?.forEach((subject) => {
        const subjectTree: EvaluationTree = {
          subject: subject,
          history: [] as Evaluation[],
        };
        response.data._items.forEach((history) => {
          if (history.subject === subject) {
            subjectTree.history.push(history);
          }
        });
        evaluationTree.push(subjectTree);
      });
      return evaluationTree;
    } catch (error) {
      NotifyHelper.notifyError(Messages.FETCH_EVALUATIONS_HISTORY_ERROR);
      console.error(error);
    }
  }
  public async fetchBestQuestion({
    question,
    theta,
    subject,
    evaluation_id,
    qids,
    answer,
    answer_ammount,
  }: BestQuestionParans): Promise<BestQuestionResponse | undefined> {
    try {
      const loginData = Authentication.loggedUser();
      if (!loginData) return;
      const response = await api.post(
        BEST_QUESTION_ENDPOINT,
        {
          question,
          theta,
          subject,
          contest: loginData.contest._id,
          evaluation_id,
          qids,
          answer,
          answer_ammount,
          user_email: loginData.user.email,
        },
        {
          headers: {
            ContestId: loginData.contest._id,
            UserEmail: loginData.user.email,
          },
        }
      );
      return response.data as BestQuestionResponse;
    } catch (error) {
      NotifyHelper.notifyError(Messages.FETCH_BEST_QUESTION_ERROR);
    }
  }
  public async saveEvaluation({
    eid,
    subject,
    theta,
  }: {
    eid: string;
    subject: string;
    theta: number;
  }): Promise<MongoAttributes | undefined> {
    try {
      const sessionData = Authentication.loggedUser();
      if (!sessionData) return;
      const response = await api.post<MongoAttributes>(
        HISTORY_EVALUATION_ENDPOINT,
        {
          eid,
          subject,
          theta,
          user_email: sessionData.user.email,
        }
      );
      await this.updateJourneyEvaluation();
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }
  public async updateJourneyEvaluation() {
    const sessionData = Authentication.loggedUser();
    if (!sessionData) return;
    if (sessionData.journey._id && !sessionData.journey.evaluation) {
      const id = sessionData.journey._id;
      await Journey.update(id, {
        evaluation: true,
      });
      sessionData.journey.evaluation = true;
      Authentication.storeUser(sessionData);
    }
  }
}

export default new Evaluations();
